import {
    Component,
    ElementRef,
    inject,
    input,
    viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import {
    FeatureHintComponent,
    FeatureType,
} from '@app/shared/components/feature-hints/feature-hint/feature-hint.component';

@Component({
    selector: 'app-feature-hint-tooltip',
    imports: [MatIcon],
    templateUrl: './feature-hint-tooltip.component.html',
    styleUrl: './feature-hint-tooltip.component.scss',
})
export class FeatureHintTooltipComponent {
    featureType = input.required<FeatureType>();

    private overlay = inject(Overlay);
    private overlayRef?: OverlayRef;
    tooltipTrigger = viewChild<ElementRef>('tooltipTrigger');

    private tooltipCloseTimer: ReturnType<typeof setTimeout> | null = null;

    showCustomAsnTooltip() {
        // Clear any pending close timer
        this.clearTooltipCloseTimer();

        // If tooltip already visible, don't recreate it
        if (this.overlayRef) {
            return;
        }

        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.tooltipTrigger()?.nativeElement)
            .withPositions([
                {
                    originX: 'end',
                    originY: 'center',
                    overlayX: 'start',
                    overlayY: 'center',
                    offsetX: 30,
                },
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 8,
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                    offsetY: -8,
                },
            ])
            .withDefaultOffsetX(8)
            .withDefaultOffsetY(0);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
        });

        const tooltipPortal = new ComponentPortal(FeatureHintComponent);
        const componentRef = this.overlayRef.attach(tooltipPortal);
        componentRef.setInput('featureType', this.featureType());
        componentRef.setInput('isTooltipContent', true);

        // Add mouseenter listener to overlay to keep it open when hovering over the tooltip
        const overlayElement = this.overlayRef.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseenter', () =>
                this.onTooltipMouseEnter()
            );
            overlayElement.addEventListener('mouseleave', () =>
                this.onTooltipMouseLeave()
            );
        }
    }

    closeCustomTooltip() {
        // Delay the close action to allow checking if tooltip is hovered
        this.tooltipCloseTimer = setTimeout(() => {
            if (this.overlayRef) {
                this.overlayRef.dispose();
                this.overlayRef = undefined;
            }
        }, 100);
    }

    private onTooltipMouseEnter() {
        // Clear any pending close timer when tooltip is hovered
        this.clearTooltipCloseTimer();
    }

    private onTooltipMouseLeave() {
        // Close tooltip when mouse leaves it
        this.closeCustomTooltip();
    }

    private clearTooltipCloseTimer() {
        if (this.tooltipCloseTimer !== null) {
            clearTimeout(this.tooltipCloseTimer);
            this.tooltipCloseTimer = null;
        }
    }
}
